/**
 * assets/quiz.js
 * Perfect Locks - Hair Match Quiz
 *
 * Alpine.js v3 data functions.
 * Alpine is already bundled in the theme (assets/alpine.js).
 * Do NOT load Alpine again from CDN.
 *
 * Two components:
 *   hairQuiz()          - Main quiz state machine
 *   quizProductCard()   - Per-card swatch/length picker + cart
 */

// ─── Questions ────────────────────────────────────────────────────────────────
const QUIZ_QUESTIONS = [
  {
    id: 'goal',
    step: 0,
    question: "What's your main hair goal?",
    subtitle: 'Select everything that applies.',
    type: 'multi',
    options: [
      { value: 'length',      label: 'More Length',        desc: 'I want it longer' },
      { value: 'volume',      label: 'More Volume',        desc: 'Fuller, thicker hair' },
      { value: 'color',       label: 'Color Dimension',    desc: 'Highlights, balayage, ombre - without commitment' },
      { value: 'thinning',    label: 'Thinning Coverage',  desc: 'Fullness where hair is sparse or thin on top' },
      { value: 'full_cover',  label: 'Full Coverage',      desc: 'Alopecia, significant loss, or a complete new look' },
      { value: 'quick_look',  label: 'Quick Style Change', desc: 'Instant ponytail, bangs, or a fun new look - removable' },
      { value: 'protection',  label: 'Protect My Hair',    desc: 'Give natural hair a rest' },
    ],
  },
  {
    id: 'desired_length',
    step: 1,
    question: "What's your goal length?",
    subtitle: 'Select one.',
    type: 'single',
    options: [
      { value: 'shoulder',         label: 'Shoulder length',   desc: null },
      { value: 'below_shoulders',  label: 'Below shoulders',   desc: null },
      { value: 'mid_back',         label: 'Mid-back',          desc: null },
      { value: 'waist',            label: 'Waist or longer',   desc: null },
    ],
  },
  {
    id: 'hair_texture',
    step: 2,
    question: 'How thick is your hair?',
    subtitle: 'Per strand, not overall volume.',
    type: 'single',
    options: [
      { value: 'fine',      label: 'Fine',      desc: 'Delicate, almost weightless strands' },
      { value: 'medium',    label: 'Medium',    desc: 'Average, holds a style well' },
      { value: 'thick',     label: 'Thick',     desc: 'Noticeably coarser strands' },
      { value: 'not_sure',  label: "Not sure",  desc: null },
    ],
  },
  {
    id: 'hair_type',
    step: 3,
    question: "What's your natural texture?",
    subtitle: 'Select one.',
    type: 'single',
    options: [
      { value: 'straight',       label: 'Straight',       desc: 'Types 1a - 1c: Flat to full with a slight bend' },
      { value: 'wavy',           label: 'Wavy',           desc: '2A-2B: Loose S-waves to defined beachy waves' },
      { value: 'curly',          label: 'Curly',          desc: '2C-3A: Springy ringlets to tight corkscrews' },
      { value: 'tight_curly',    label: 'Tight Curly',    desc: '3B: Very tightly coiled with maximum shrinkage' },
      { value: 'kinky_curly',    label: 'Kinky Curly',    desc: '3C-4C: Dense Z-shaped kinks with little sheen' },
      { value: 'kinky_straight', label: 'Kinky Straight', desc: '4A-4C: Tight S-coils that stretch almost straight' },
    ],
  },
  {
    id: 'hair_color',
    step: 4,
    question: "What's your current hair color?",
    subtitle: 'Helps us recommend shades that blend naturally.',
    type: 'single',
    options: [
      { value: 'black',        label: 'Black',                    desc: null },
      { value: 'brown',        label: 'Brown',                    desc: null },
      { value: 'blonde',       label: 'Blonde',                   desc: null },
      { value: 'red',          label: 'Red',                      desc: null },
      { value: 'dimensional',  label: 'Dimensional',              desc: 'Highlights, balayage, ombre' },
      { value: 'gray',         label: 'Gray / Silver',            desc: null },
    ],
  },
  {
    id: 'method',
    step: 5,
    question: 'How do you plan to wear your extensions?',
    subtitle: "Not sure? We'll figure it out together.",
    type: 'single',
    options: [
      { value: 'diy',       label: 'DIY at home',          desc: 'Clip-ins, halos, tape-ins, ponytails' },
      { value: 'salon',     label: 'Salon install',        desc: 'Wefts, K-tips, I-tips, U-tips, beaded' },
      { value: 'not_sure',  label: 'Not sure - guide me!', desc: null },
    ],
  },
  {
    id: 'hair_health',
    step: 6,
    question: 'How is your hair health right now?',
    subtitle: 'Helps us recommend the safest method.',
    type: 'single',
    options: [
      { value: 'healthy',          label: 'Healthy',          desc: 'Strong, minimal breakage' },
      { value: 'slightly_damaged', label: 'Slightly Damaged', desc: 'Some dryness or color treated' },
      { value: 'damaged',          label: 'Damaged',          desc: 'Bleached or over-processed' },
      { value: 'very_damaged',     label: 'Very Damaged',     desc: 'Significant breakage' },
    ],
  },
  {
    id: 'budget',
    step: 7,
    question: "What's your extension budget?",
    subtitle: "We have great options at every range.",
    type: 'single',
    options: [
      { value: 'low',     label: 'Under $150',   desc: 'Best value clip-ins and halos' },
      { value: 'mid',     label: '$150 - $300',  desc: 'Mid-range quality' },
      { value: 'upper',   label: '$300 - $500',  desc: 'Premium DIY methods' },
      { value: 'premium', label: '$500+',        desc: 'Salon-quality installs' },
    ],
  },
];

// ─── Method config ────────────────────────────────────────────────────────────
const METHOD_CONFIG = {
  clip_in: {
    label: 'Clip-In Extensions',
    collectionUrl: '/collections/clip-in-hair-extensions',
    reason: 'Maximum flexibility with zero damage. In and out in minutes.',
  },
  tape_in: {
    label: 'Tape-In Extensions',
    collectionUrl: '/collections/tape-in-hair-extensions',
    reason: 'The sweet spot of seamless wear and easy maintenance. Lasts 6-8 weeks per install.',
  },
  halo: {
    label: 'Halo Extensions',
    collectionUrl: '/collections/halo-hair-extensions',
    reason: 'On in 60 seconds, zero damage, completely invisible wire.',
  },
  fusion: {
    label: 'Fusion / K-Tip Extensions',
    collectionUrl: '/collections/fusion-hair-extensions',
    reason: 'The most natural-looking permanent option. Grows seamlessly with your hair.',
  },
  u_tip: {
    label: 'U-Tip Extensions',
    collectionUrl: '/collections/u-tip-hair-extensions',
    reason: 'A keratin bond applied with a flat iron - great for thick or coarse hair that needs extra hold.',
  },
  weft: {
    label: 'Hand-Tied Wefts',
    collectionUrl: '/collections/hand-tied-wefts',
    reason: 'Salon-installed for the most seamless, voluminous result.',
  },
  beaded_weft: {
    label: 'Beaded Weft Extensions',
    collectionUrl: '/collections/beaded-weft-hair-extensions',
    reason: 'No glue, no heat - micro-beads hold rows of weft flat against your head. Completely chemical-free.',
  },
  hybrid_weft: {
    label: 'Hybrid Weft Extensions',
    collectionUrl: '/collections/hybrid-weft-extensions',
    reason: 'The thinnest, flattest weft available - hand-tied and machine-sewn for a barely-there install.',
  },
  machine_weft: {
    label: 'Machine Weft Extensions',
    collectionUrl: '/collections/machine-weft-hair-extensions',
    reason: 'Durable, full-volume wefts at a more accessible price point. Salon-sewn for longevity.',
  },
  ponytail: {
    label: 'Clip-In Ponytail Extensions',
    collectionUrl: '/collections/clip-in-ponytail-extensions',
    reason: 'One clip, instant long ponytail. No commitment, no styling required.',
  },
  bangs: {
    label: 'Clip-In Bangs',
    collectionUrl: '/collections/clip-in-bangs',
    reason: 'Try fringe without the haircut. On in seconds, off in seconds.',
  },
  closure: {
    label: 'Closures & Frontals',
    collectionUrl: '/collections/closures-frontals',
    reason: 'A natural-looking hairline and part for protective styles and full sew-in looks.',
  },
  topper: {
    label: 'Hair Toppers',
    collectionUrl: '/collections/crown-hair-toppers',
    reason: 'Clip-on coverage exactly where you need it most - no full install required.',
  },
  wig: {
    label: 'Wigs',
    collectionUrl: '/collections/real-human-hair-wigs',
    reason: 'Full coverage and instant transformation with zero commitment to your natural hair.',
  },
};

// ─── Texture-aware collection routing ─────────────────────────────────────────
// Maps [method][hair_type] → the most specific collection handle available.
// Falls back to the method's base collection if no texture-specific one exists.
// Based on the full Perfect Locks catalog.
const COLLECTION_ROUTING = {
  clip_in: {
    curly:          'curly-clip-in-hair-extensions',
    kinky_curly:    'kinky-curly-hair-extensions',
    tight_curly:    'kinky-curly-hair-extensions',
    kinky_straight: 'textured-hair-extensions',
    wavy:           'wavy-hair-extensions',
    straight:       'seamless-clip-in-hair-extensions',
    _default:       'clip-in-hair-extensions',
  },
  tape_in: {
    curly:          'curly-hair-extensions',
    kinky_curly:    'kinky-curly-hair-extensions',
    tight_curly:    'kinky-curly-hair-extensions',
    kinky_straight: 'relaxed-straight-hair-extensions',
    wavy:           'wavy-hair-extensions',
    straight:       'invisible-tape-ins',
    _default:       'tape-in-hair-extensions',
  },
  halo: {
    curly:          'curly-halo-hair-extensions',
    kinky_curly:    'curly-halo-hair-extensions',
    tight_curly:    'curly-halo-hair-extensions',
    _default:       'halo-hair-extensions',
  },
  fusion: {
    curly:          'curly-fusion-hair-extensions',
    kinky_curly:    'curly-fusion-hair-extensions',
    tight_curly:    'curly-fusion-hair-extensions',
    straight:       'i-tip-hair-extensions',
    _default:       'fusion-hair-extensions',
  },
  u_tip: {
    _default: 'u-tip-hair-extensions',
  },
  weft: {
    curly:          'curly-weft-hair-extensions',
    kinky_curly:    'curly-weft-hair-extensions',
    tight_curly:    'curly-weft-hair-extensions',
    straight:       'machine-weft-hair-extensions',
    wavy:           'hand-tied-wefts',
    _default:       'hand-tied-wefts',
  },
  beaded_weft: {
    _default: 'beaded-weft-hair-extensions',
  },
  hybrid_weft: {
    _default: 'hybrid-weft-extensions',
  },
  machine_weft: {
    _default: 'machine-weft-hair-extensions',
  },
  ponytail: {
    curly:          'curly-ponytail-extensions',
    kinky_curly:    'curly-ponytail-extensions',
    tight_curly:    'curly-ponytail-extensions',
    _default:       'clip-in-ponytail-extensions',
  },
  bangs: {
    _default: 'clip-in-bangs',
  },
  closure: {
    _default: 'closures-frontals',
  },
  topper: {
    _default: 'crown-hair-toppers',
  },
  wig: {
    curly:        'curly-wigs',
    kinky_curly:  'curly-wigs',
    tight_curly:  'curly-wigs',
    _default:     'real-human-hair-wigs',
  },
};

// ─── Color map ────────────────────────────────────────────────────────────────
// Maps quiz color answers to regex patterns for "Number + Name" variant formats
// e.g. "#2 Dark Brown", "#1B Off Black", "#613 Bleach Blonde"
const COLOR_MAP = {
  black:       [/\b1\s*b?\b/i, /jet\s*black/i, /off\s*black/i, /natural\s*black/i],
  brown:       [/\b[2-6]\b/, /dark\s*brown/i, /medium\s*brown/i, /light\s*brown/i,
                /chestnut/i, /chocolate/i, /walnut/i, /espresso/i, /mahogany/i, /mocha/i],
  blonde:      [/\b(22|27|60|613|88)\b/, /blonde/i, /blond/i, /platinum/i,
                /ash\s*blonde/i, /golden\s*blonde/i, /honey\s*blonde/i, /dirty\s*blonde/i],
  red:         [/\b(33|99j|130)\b/i, /\bred\b/i, /auburn/i, /copper/i, /ginger/i, /cherry/i, /burgundy/i],
  dimensional: [/balayage/i, /ombre/i, /highlight/i, /dimensional/i, /sombre/i, /rooted/i, /dip\s*dye/i],
  gray:        [/\bgra[ey]\b/i, /silver/i, /salt\s*(and|&|n)\s*pepper/i],
};

// ─── Texture tag map ──────────────────────────────────────────────────────────
// Maps quiz answers to the product tag values used in your Shopify store.
// Update these values to match your exact tag naming convention.
const TEXTURE_TAG_MAP = {
  // hair_texture (strand thickness)
  fine:     ['fine', 'fine-hair', 'fine hair'],
  medium:   ['medium', 'medium-hair', 'normal'],
  thick:    ['thick', 'thick-hair', 'coarse', 'coarse-hair'],
  not_sure: [],
  // hair_type (curl pattern)
  straight:       ['straight', 'straight-hair'],
  wavy:           ['wavy', 'wavy-hair'],
  curly:          ['curly', 'curly-hair'],
  kinky_straight: ['kinky-straight', 'kinky straight', 'type-4a', '4a'],
  kinky_curly:    ['kinky-curly', 'kinky curly', 'type-4b', '4b'],
  tight_curly:    ['tight curly', 'tight-curly', 'tight_curly', 'type-4c', '4c'],
};

// ─── Utility helpers ──────────────────────────────────────────────────────────

// Replicates Liquid's | handleize filter
function handleize(str) {
  return String(str)
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

function escapeHtml(str) {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

// Returns a Shopify CDN sized image URL from a raw src - no crop, preserves full image
function shopifyImgUrl(src, size = '600x') {
  if (!src) return '';
  // Remove existing size modifier if present, then add new one
  return src
    .replace(/_\d+x\d*(_crop_\w+)?(\.[a-zA-Z]+)/, '$2')
    .replace(/(\.[a-zA-Z]+)(\?.*)?$/, `_${size}$1$2`);
}

// ─── Product scoring ──────────────────────────────────────────────────────────
// Scores a product from products.json against the user's quiz answers.
// Higher score = better match. Used to rank fetched products before slicing top 2.
function scoreProduct(product, answers) {
  let score = 0;
  const tags = (product.tags || []).map(t => t.toLowerCase().trim());

  // Strand thickness match (+3)
  const textureTags = TEXTURE_TAG_MAP[answers.hair_texture] || [];
  if (textureTags.length && textureTags.some(t => tags.includes(t))) score += 3;

  // Curl pattern match (+2)
  const typeTags = TEXTURE_TAG_MAP[answers.hair_type] || [];
  if (typeTags.length && typeTags.some(t => tags.includes(t))) score += 2;

  // Color match (+4) - tests variant option values against COLOR_MAP patterns.
  // Outweighs texture alone so a blonde product always beats a black one when
  // the user answered 'blonde', regardless of texture tag parity.
  if (answers.hair_color) {
    const colorPatterns = COLOR_MAP[answers.hair_color] || [];
    if (colorPatterns.length) {
      const options = (product.options || []).map(o =>
        (typeof o === 'string' ? o : o.name || '').toLowerCase()
      );
      let colorOptIdx = -1;
      options.forEach((name, i) => {
        if (['color', 'shade', 'tone', 'colour'].includes(name)) colorOptIdx = i;
      });
      if (colorOptIdx >= 0) {
        const optKey = ['option1', 'option2', 'option3'][colorOptIdx];
        const colorVals = [...new Set(
          (product.variants || []).map(v => (v[optKey] || '').toLowerCase()).filter(Boolean)
        )];
        if (colorVals.some(val => colorPatterns.some(rx => rx.test(val)))) score += 4;
      }
    }
  }

  // Has available variants (+1)
  if ((product.variants || []).some(v => v.available)) score += 1;

  // Has product images (+1)
  if ((product.images || []).length > 0) score += 1;

  return score;
}

// ─── Color swatch matching ────────────────────────────────────────────────────
// Finds the best swatch match for a quiz color answer using COLOR_MAP patterns.
// Handles "Number + Name" format like "#2 Dark Brown", "#1B Off Black".
function findBestColorSwatch(swatches, colorHint) {
  if (!colorHint || !swatches.length) return null;
  const patterns = COLOR_MAP[colorHint] || [];
  if (!patterns.length) return null;
  return swatches.find(s => patterns.some(rx => rx.test(s.name))) || null;
}

// ─── Product fetch ────────────────────────────────────────────────────────────
// Fetches products from the collection mapped to the resolved method,
// scores them against quiz answers, and returns the top 2.
// Builds Klaviyo event properties for the top 1-2 recommended products.
// Uses lightweight handle + variant URL - no price/stock since those
// go stale. Klaviyo's product catalog block handles live price at send time.
function buildProductEventProps(products, colorHint, collectionUrl) {
  const props = {
    recommended_collection_url: collectionUrl || '',
    recommended_product_count:  products.length,
  };

  products.slice(0, 2).forEach((product, i) => {
    const prefix = i === 0 ? 'top' : 'second';

    // Resolve best matching variant for the customer's color answer.
    // variantMap entries are lean (id, available, price, title only - no featured_image).
    // Look the resolved variant back up in product.variants (full products.json payload)
    // so the image and ?variant= URL always refer to the exact same variant.
    const { variantMap, swatches } = buildVariantData(product);
    const bestSwatch = findBestColorSwatch(swatches, colorHint) || swatches[0];
    const bestLength = Object.keys(variantMap)
      .filter(k => k.startsWith((bestSwatch?.value || '__default__') + '___'))
      .find(k => variantMap[k]?.available)
      || Object.keys(variantMap).find(k => variantMap[k]?.available)
      || Object.keys(variantMap)[0];
    const bestVariant = variantMap[bestLength];

    // Reverse lookup into full products.json variant to get featured_image
    const rawVariant = (product.variants || []).find(v => v.id === bestVariant?.id);

    const variantParam = bestVariant?.id ? `?variant=${bestVariant.id}` : '';
    const productUrl   = `/products/${product.handle}${variantParam}`;
    const imageUrl = rawVariant?.featured_image?.src
      ? shopifyImgUrl(rawVariant.featured_image.src, '800x')
      : product.images?.[0]?.src
        ? shopifyImgUrl(product.images[0].src, '800x')
        : '';

    props[`${prefix}_product_title`]        = product.title || '';
    props[`${prefix}_product_url`]          = productUrl;
    props[`${prefix}_product_image`]        = imageUrl;
    props[`${prefix}_product_handle`]       = product.handle || '';
    props[`${prefix}_product_type`]         = product.product_type || '';
    props[`${prefix}_product_color`]        = bestSwatch?.name || '';
    props[`${prefix}_product_variant_id`]   = bestVariant?.id || '';
  });

  return props;
}

// Resolve the best collection handle for a method + hair_type combination.
// Uses the texture-specific sub-collection when available, falls back to base.
function resolveCollectionHandle(method, answers) {
  const cfg       = window.QUIZ_CONFIG || {};
  const overrides = cfg.collectionHandles || {};
  // Admin-configured override takes highest priority
  if (overrides[method]) return overrides[method];
  // Texture-aware routing
  const hairType  = answers?.hair_type || '_default';
  const routing   = COLLECTION_ROUTING[method] || {};
  return routing[hairType] || routing['_default'] || METHOD_CONFIG[method]?.collectionUrl?.replace('/collections/', '');
}

// Fetch ALL products from a collection (paginates up to 250 per page x 4 pages = 1000 max)
// then scores and returns the top 2.
async function fetchProductsForMethod(method, answers) {
  const handle = resolveCollectionHandle(method, answers);

  if (!handle) {
    console.warn(`[Quiz] No collection handle for method "${method}"`);
    return [];
  }

  try {
    // Payload budget. products.json returns full variant arrays, which on hair
    // extension collections is megabytes per page. Two pages is enough for
    // scoring; raise only if recommendations visibly degrade.
    const PAGE_LIMIT = 250;
    const MAX_PAGES  = 2;

    const pages = [];
    for (let page = 1; page <= MAX_PAGES; page++) {
      const res = await fetch(`/collections/${handle}/products.json?limit=${PAGE_LIMIT}&page=${page}`);
      if (!res.ok) break;
      const { products = [] } = await res.json();
      if (!products.length) break;
      pages.push(...products);
      if (products.length < PAGE_LIMIT) break; // last page
    }

    if (!pages.length) {
      // Fallback to base method collection if texture-specific one was empty
      const baseHandle = COLLECTION_ROUTING[method]?.['_default'];
      if (baseHandle && baseHandle !== handle) {
        const res = await fetch(`/collections/${baseHandle}/products.json?limit=250`);
        if (res.ok) {
          const { products = [] } = await res.json();
          pages.push(...products);
        }
      }
    }

    if (!pages.length) return [];

    // Deduplicate by product id
    const seen = new Set();
    const unique = pages.filter(p => seen.has(p.id) ? false : seen.add(p.id));

    return unique
      .filter(p => (p.variants || []).some(v => v.available))
      .map(p => ({ product: p, score: scoreProduct(p, answers) }))
      .sort((a, b) => b.score - a.score)
      .slice(0, 3)
      .map(s => s.product);
  } catch (err) {
    console.error('[Quiz] fetchProductsForMethod error:', err);
    return [];
  }
}

// ─── Variant data parser ──────────────────────────────────────────────────────
// Parses a Shopify products.json product object into the variantMap / swatches /
// lengths / swatchImages structure expected by quizProductCard().
function buildVariantData(product) {
  const options = (product.options || []).map(o =>
    typeof o === 'string' ? o : o.name || ''
  );

  let colorIdx = -1, lengthIdx = -1;
  options.forEach((name, i) => {
    const l = name.toLowerCase();
    if (['color', 'shade', 'tone', 'colour'].includes(l)) colorIdx = i;
    if (['length', 'size'].includes(l)) lengthIdx = i;
  });

  const single = product.variants.length === 1;
  const variantMap = {};
  const swatchImageMap = {};
  const seenColors = new Set();
  const seenLengths = [];

  product.variants.forEach(v => {
    const opts = [v.option1, v.option2, v.option3];

    let cVal, lVal;
    if (single) {
      cVal = '__default__'; lVal = '__default__';
    } else if (colorIdx >= 0 && lengthIdx >= 0) {
      cVal = handleize(opts[colorIdx] || '');
      lVal = opts[lengthIdx] || '__default__';
    } else if (colorIdx >= 0) {
      cVal = handleize(opts[colorIdx] || '');
      lVal = '__default__';
    } else if (lengthIdx >= 0) {
      cVal = '__default__';
      lVal = opts[lengthIdx] || '__default__';
    } else {
      cVal = '__default__'; lVal = '__default__';
    }

    variantMap[`${cVal}___${lVal}`] = {
      id:               v.id,
      available:        v.available,
      price:            parseFloat(v.price) || 0,             // products.json returns decimal dollars e.g. "359.00"
      compare_at_price: parseFloat(v.compare_at_price) || 0,
      title:            v.title,
    };

    // First image seen for this color becomes its swatch image
    if (!seenColors.has(cVal)) {
      seenColors.add(cVal);
      const rawSrc = v.featured_image?.src || product.images?.[0]?.src || '';
      swatchImageMap[cVal] = shopifyImgUrl(rawSrc);
    }

    // Track length order
    if (lVal !== '__default__' && !seenLengths.includes(lVal)) {
      seenLengths.push(lVal);
    }
  });

  // Build deduplicated swatches in variant order
  const swatches = [];
  const seenSwatchVals = new Set();
  const swatchStyles = (window.QUIZ_CONFIG || {}).swatchStyles || {};

  product.variants.forEach(v => {
    const opts = [v.option1, v.option2, v.option3];
    const cName = single ? 'Default' : (colorIdx >= 0 ? (opts[colorIdx] || 'Default') : 'Default');
    const cVal  = single ? '__default__' : handleize(cName);
    if (!seenSwatchVals.has(cVal)) {
      seenSwatchVals.add(cVal);
      // Look up style from theme's color_swatch_config (exported via QUIZ_CONFIG.swatchStyles)
      // Key is the color name lowercased, matching how color-swatch-style.liquid resolves it
      const swatchStyle = swatchStyles[cName.toLowerCase()] || '';
      swatches.push({ name: cName, value: cVal, swatch_image: swatchImageMap[cVal] || '', swatchStyle });
    }
  });

  const lengths = seenLengths.length ? seenLengths : ['__default__'];

  return { variantMap, swatches, lengths, swatchImages: swatchImageMap };
}

// ─── Card HTML builder ────────────────────────────────────────────────────────
// Builds the HTML string for a dynamic product card.
// Stores variant data in window.__quizCardData keyed by cardId so Alpine can
// access it without complex attribute escaping.
function buildCardHTML(product, isPrimary, colorHint) {
  const cardId = `qpc_${product.id}`;
  window.__quizCardData = window.__quizCardData || {};
  window.__quizCardData[cardId] = buildVariantData(product);

  const title       = escapeHtml(product.title || '');
  const productUrl  = `/products/${product.handle}`;
  const productType = escapeHtml(product.product_type || '');
  const rawDesc     = (product.body_html || '').replace(/<[^>]+>/g, '');
  const desc        = escapeHtml(rawDesc.length > 110 ? rawDesc.substring(0, 110) + '...' : rawDesc);
  const featuredSrc = shopifyImgUrl(product.images?.[0]?.src || '');
  const badgeHtml   = isPrimary
    ? `<div class="quiz-product-card__badge" aria-label="Product badge: Top Pick">Top Pick</div>`
    : '';

  return `
<article
  class="quiz-product-card${isPrimary ? ' quiz-product-card--primary' : ''}"
  x-data="quizProductCard('${cardId}', '${colorHint || ''}')"
  x-init="initCard()"
  aria-label="${title}"
>
  ${badgeHtml}

  <div class="quiz-product-card__image-wrap">
    <img
      :src="currentImage || '${featuredSrc}'"
      :alt="'${title} - ' + selectedSwatchName"
      class="quiz-product-card__image"
      width="600"
      height="750"
      loading="lazy"
    >
  </div>

  <div class="quiz-product-card__body">
    <p class="quiz-product-card__tag">${productType}</p>

    <div class="quiz-product-card__title-row">
      <h3 class="quiz-product-card__title">
        <a href="${productUrl}" class="quiz-product-card__title-link" tabindex="0">${title}</a>
      </h3>
      <div class="quiz-product-card__price" aria-live="polite"
        x-effect="
          $el.innerHTML = formatPrice(currentPrice);
          $el.setAttribute('aria-label', 'Price: ' + $el.textContent.trim());
        "
      ></div>
    </div>

    <p class="quiz-product-card__desc">${desc}</p>

    <div class="quiz-product-card__swatches" x-show="swatches.length > 1 && swatches[0].value !== '__default__'">
      <div class="quiz-product-card__option-header">
        <span class="quiz-product-card__option-label" id="color-label-${product.id}">Color</span>
        <span class="quiz-product-card__option-value" x-text="selectedSwatchName" aria-live="polite"></span>
      </div>
      <div class="color-swatch-list quiz-swatch-list" role="group" aria-labelledby="color-label-${product.id}">
        <template x-for="swatch in swatches" :key="swatch.value">
          <div class="color-swatch" :class="{ 'quiz-swatch--active': selectedColor === swatch.value }">
            <button
              type="button"
              class="color-swatch__item quiz-swatch__btn"
              :aria-pressed="selectedColor === swatch.value"
              :aria-label="swatch.name + (selectedColor === swatch.value ? ' (selected)' : '')"
              :title="swatch.name"
              @click="selectColor(swatch.value)"
              :style="swatch.swatchStyle || ('background-color: #ccc')"
            >
              <span class="visually-hidden" x-text="swatch.name"></span>
            </button>
          </div>
        </template>
      </div>
    </div>

    <div class="quiz-product-card__lengths" x-show="lengths.length > 1 && lengths[0] !== '__default__'">
      <div class="quiz-product-card__option-header">
        <span class="quiz-product-card__option-label" id="length-label-${product.id}">Length</span>
      </div>
      <div class="quiz-product-card__length-list" role="group" aria-labelledby="length-label-${product.id}">
        <template x-for="len in lengths" :key="len">
          <button
            type="button"
            class="quiz-product-card__length"
            :class="{
              'quiz-product-card__length--active': selectedLength === len,
              'quiz-product-card__length--unavailable': !isLengthAvailable(len)
            }"
            :aria-pressed="selectedLength === len"
            :aria-label="len + (selectedLength === len ? ' (selected)' : '') + (!isLengthAvailable(len) ? ' - out of stock' : '')"
            :disabled="!isLengthAvailable(len)"
            @click="selectLength(len)"
          ><span x-text="len"></span></button>
        </template>
      </div>
    </div>

    <div x-show="!currentVariantAvailable" role="alert" aria-live="polite">
      <p class="quiz-product-card__unavailable" x-show="!bisSubscribed">
        This combination is out of stock.
      </p>
      <p class="quiz-product-card__bis-confirm" x-show="bisSubscribed">
        &#10003; We'll email you when this is back in stock.
      </p>
    </div>

    <div class="quiz-product-card__actions">
      <button
        type="button"
        class="quiz-btn quiz-btn--primary quiz-btn--flex"
        :class="{ 'quiz-btn--added': addedToCart }"
        :disabled="(!currentVariantAvailable && !addedToCart) || addingToCart"
        :aria-busy="addingToCart"
        :aria-label="addedToCart ? 'Added to your cart' : \`Add to cart: \${selectedSwatchName}\${selectedLength !== '__default__' ? ', ' + selectedLength : ''}\`"
        @click="addToCart()"
      >
        <span x-show="!addedToCart && !addingToCart">Add to Cart</span>
        <span x-show="addingToCart" aria-hidden="true">Adding...</span>
        <span x-show="addedToCart">&#10003; Added</span>
      </button>
      <a
        :href="\`${productUrl}?variant=\` + currentVariantId"
        class="quiz-btn quiz-btn--ghost"
        :aria-label="\`View full details for ${title}\`"
        @click="viewProduct()"
      >View</a>
    </div>

  </div>
</article>`;
}

// ─── Results logic ────────────────────────────────────────────────────────────
function computeResults(answers) {
  const {
    goal, desired_length, hair_texture, hair_type,
    hair_color, method, hair_health, budget,
  } = answers;

  const goalArr     = Array.isArray(goal) ? goal : (goal ? [goal] : []);
  const damageRisk  = hair_health === 'damaged' || hair_health === 'very_damaged';
  const wantsSalon  = method === 'salon';
  const needsStylist = wantsSalon || budget === 'premium';

  // ── Method decision tree ──────────────────────────────────────────────────
  let resolvedMethod;

  // 1. Quick style change: ponytail is #1 pick; bangs if no length goal
  if (goalArr.includes('quick_look') && !goalArr.includes('length') && !goalArr.includes('volume')) {
    resolvedMethod = goalArr.includes('color') ? 'ponytail' : 'bangs';

  } else if (goalArr.includes('quick_look') && !goalArr.includes('volume')) {
    resolvedMethod = 'ponytail';

  // 2. Full-coverage goals → wig
  } else if (goalArr.includes('full_cover')) {
    resolvedMethod = 'wig';

  // 3. Thinning only (no length/volume) → topper
  } else if (goalArr.includes('thinning') && !goalArr.includes('length') && !goalArr.includes('volume')) {
    resolvedMethod = 'topper';

  // 4. Salon install
  } else if (wantsSalon) {
    if (budget === 'premium') {
      // Premium salon: beaded weft for chemical-free, hybrid for ultra-thin, fusion for natural grow-out
      // Route by texture: coarse/thick hair → u-tip (strongest bond), fine → hybrid weft, else beaded weft
      if (hair_texture === 'thick') {
        resolvedMethod = 'u_tip';
      } else if (hair_texture === 'fine') {
        resolvedMethod = 'hybrid_weft';
      } else {
        resolvedMethod = 'beaded_weft';
      }
    } else {
      // Standard salon: hand-tied weft default, machine weft for budget-conscious
      resolvedMethod = budget === 'mid' ? 'machine_weft' : 'weft';
    }

  // 5. Very damaged → halo only
  } else if (hair_health === 'very_damaged') {
    resolvedMethod = 'halo';

  // 6. Thinning + wants length/volume → halo (gentlest on sparse areas)
  } else if (goalArr.includes('thinning')) {
    resolvedMethod = 'halo';

  // 7. Fine + healthy → tape-in (lies flattest, most invisible on fine hair)
  } else if (hair_texture === 'fine' && hair_health !== 'damaged') {
    resolvedMethod = 'tape_in';

  // 8. Damaged (not very) → clip-in (fully removable, zero stress on hair)
  } else if (damageRisk) {
    resolvedMethod = 'clip_in';

  // 9. Protection goal without length → halo (easiest on/off)
  } else if (goalArr.includes('protection') && !goalArr.includes('length')) {
    resolvedMethod = 'halo';

  // 10. Premium DIY budget → tape-in
  } else if (budget === 'premium') {
    resolvedMethod = 'tape_in';

  // 11. Upper DIY budget with clear intent → tape-in
  } else if (budget === 'upper' && method !== 'not_sure') {
    resolvedMethod = 'tape_in';

  // 12. Default → clip-in
  } else {
    resolvedMethod = 'clip_in';
  }

  const methodCfg = METHOD_CONFIG[resolvedMethod] || METHOD_CONFIG.clip_in;

  // ── Color-based secondary collection recommendations ──────────────────────
  const colorRecs = [];
  if (hair_color === 'dimensional') {
    colorRecs.push({ label: 'Balayage Extensions', collection: '/collections/balayage-hair-extensions' });
    colorRecs.push({ label: 'Highlights', collection: '/collections/highlights-hair-extensions' });
  }
  if (hair_type === 'curly' || hair_type === 'kinky_curly' || hair_type === 'tight_curly') {
    colorRecs.push({ label: 'Curly & Wavy Extensions', collection: '/collections/curly-hair-extensions' });
  }
  if (hair_color === 'black' || hair_color === 'brown') {
    colorRecs.push({ label: 'Color Match Tool', collection: '/pages/color-matching' });
    colorRecs.push({ label: 'Rooted & Natural Shades', collection: '/collections/rooted-tape-in-hair-extensions' });
  }
  if (resolvedMethod === 'topper' || resolvedMethod === 'wig') {
    colorRecs.push({ label: 'All Toppers', collection: '/collections/crown-hair-toppers' });
    colorRecs.push({ label: 'All Wigs', collection: '/collections/real-human-hair-wigs' });
  }
  if (['weft', 'beaded_weft', 'hybrid_weft', 'machine_weft'].includes(resolvedMethod)) {
    colorRecs.push({ label: 'Closures & Frontals', collection: '/collections/closures-frontals' });
  }
  if (resolvedMethod === 'ponytail') {
    colorRecs.push({ label: 'All Ponytail Extensions', collection: '/collections/clip-in-ponytail-extensions' });
    colorRecs.push({ label: 'Curly Ponytails', collection: '/collections/curly-ponytail-extensions' });
  }

  // ── Personal message ──────────────────────────────────────────────────────
  const goalText = goalArr.includes('volume') && goalArr.includes('length')
    ? 'length and volume'
    : goalArr.includes('length') ? 'more length'
    : goalArr.includes('volume') ? 'more volume'
    : goalArr.includes('thinning') ? 'fuller coverage'
    : goalArr.includes('full_cover') ? 'complete coverage'
    : goalArr.includes('quick_look') ? 'an instant style change'
    : goalArr.includes('protection') ? 'protecting your natural hair'
    : 'your hair goals';

  const healthNote = damageRisk
    ? ' Since your hair needs extra care right now, we chose the gentlest attachment method available.'
    : '';

  const personalMessage = `${methodCfg.label} are your ideal match for ${goalText}. ${methodCfg.reason}${healthNote}`;

  // ── Klaviyo profile payload ───────────────────────────────────────────────
  // hair_type stays a machine slug for routing/segmentation. hair_type_label is
  // the human-readable form - ALWAYS use this one in email/SMS copy, otherwise
  // merge tags render as "kinky_curly".
  const HAIR_TYPE_LABELS = {
    straight:       'straight',
    wavy:           'wavy',
    curly:          'curly',
    tight_curly:    'tightly curled',
    kinky_curly:    'kinky curly',
    kinky_straight: 'kinky straight',
  };

  const klaviyoProfile = {
    hair_goal:                  goalArr.join(','),
    desired_length:             desired_length || '',
    hair_texture:               hair_texture || '',
    hair_type:                  hair_type || '',
    hair_type_label:            HAIR_TYPE_LABELS[hair_type] || 'textured',
    hair_color_profile:         hair_color || '',
    preferred_install_method:   method || '',
    hair_health:                hair_health || '',
    budget_range:               budget || '',
    quiz_recommended_method:    resolvedMethod,
    needs_stylist:              needsStylist,
    quiz_completed:             true,
    quiz_completed_at:          new Date().toISOString(),
    quiz_source:                'perfect-match-quiz',
  };

  return {
    method: resolvedMethod,
    methodLabel: methodCfg.label,
    collectionUrl: methodCfg.collectionUrl, // Browse-all link; fetchProductsForMethod resolves texture-specific handle
    colorRecs,
    needsStylist,
    personalMessage,
    klaviyoProfile,
  };
}

// ─── Klaviyo ──────────────────────────────────────────────────────────────────

// Identify/update a profile. Custom properties go under `properties` in the
// REST API; _learnq accepts them at root level.
async function klaviyoIdentify(email, profileProps, publicKey) {
  if (!email) return;
  if (!publicKey) {
    console.warn('[Quiz] No Klaviyo public key - skipping identify.');
    return;
  }

  // Klaviyo has loaded and replaced the plain Array shim: use _learnq directly.
  // If still a shim (consent-gated / not yet loaded), use REST so data isn't lost.
  if (typeof window._learnq !== 'undefined' && !Array.isArray(window._learnq)) {
    try { window._learnq.push(['identify', { $email: email, ...profileProps }]); } catch (_) {}
    return;
  }

  try {
    const res = await fetch(
      `https://a.klaviyo.com/client/profiles/?company_id=${encodeURIComponent(publicKey)}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', revision: '2024-10-15' },
        body: JSON.stringify({
          data: {
            type: 'profile',
            attributes: {
              email,
              properties: profileProps,   // custom props live here in v3 REST
            },
          },
        }),
      }
    );
    if (!res.ok) console.warn('[Quiz] Klaviyo identify HTTP', res.status, await res.text());
  } catch (err) {
    console.error('[Quiz] Klaviyo identify error:', err);
  }
}

// Track a custom event - this is what triggers Klaviyo flows.
// Must be called after identify so the profile already exists.
async function klaviyoTrackEvent(email, eventName, eventProperties, publicKey) {
  if (!email || !eventName) return;
  if (!publicKey) {
    console.warn('[Quiz] No Klaviyo public key - skipping track event.');
    return;
  }

  // Same loaded-vs-shim detection as klaviyoIdentify.
  if (typeof window._learnq !== 'undefined' && !Array.isArray(window._learnq)) {
    try { window._learnq.push(['track', eventName, eventProperties]); } catch (_) {}
    return;
  }

  try {
    const res = await fetch(
      `https://a.klaviyo.com/client/events/?company_id=${encodeURIComponent(publicKey)}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', revision: '2024-10-15' },
        body: JSON.stringify({
          data: {
            type: 'event',
            attributes: {
              metric: {
                data: { type: 'metric', attributes: { name: eventName } },
              },
              profile: {
                data: { type: 'profile', attributes: { email } },
              },
              properties: eventProperties,
              time: new Date().toISOString(),
            },
          },
        }),
      }
    );
    if (!res.ok) console.warn('[Quiz] Klaviyo track HTTP', res.status, await res.text());
  } catch (err) {
    console.error('[Quiz] Klaviyo track error:', err);
  }
}

async function klaviyoSubscribeEmail(email, listId, publicKey) {
  if (!publicKey || !email || !listId) return;
  try {
    const res = await fetch(
      `https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(publicKey)}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', revision: '2024-10-15' },
        body: JSON.stringify({
          data: {
            type: 'subscription',
            attributes: {
              profile: {
                data: {
                  type: 'profile',
                  attributes: { email },
                },
              },
              channels: { email: { marketing: { consent: 'SUBSCRIBED' } } },
            },
            relationships: {
              list: { data: { type: 'list', id: listId } },
            },
          },
        }),
      }
    );
    if (!res.ok) console.warn('[Quiz] Klaviyo email subscribe HTTP', res.status, await res.text());
  } catch (err) {
    console.error('[Quiz] Klaviyo email subscribe error:', err);
  }
}

async function klaviyoSubscribeSMS(email, phone, listId, publicKey) {
  if (!publicKey || !phone || !listId) return;
  try {
    const res = await fetch(
      `https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(publicKey)}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', revision: '2024-10-15' },
        body: JSON.stringify({
          data: {
            type: 'subscription',
            attributes: {
              profile: {
                data: {
                  type: 'profile',
                  attributes: { email, phone_number: phone },
                },
              },
              channels: { sms: { marketing: { consent: 'SUBSCRIBED' } } },
            },
            relationships: {
              list: { data: { type: 'list', id: listId } },
            },
          },
        }),
      }
    );
    if (!res.ok) console.warn('[Quiz] Klaviyo SMS subscribe HTTP', res.status, await res.text());
  } catch (err) {
    console.error('[Quiz] Klaviyo SMS subscribe error:', err);
  }
}

// Subscribe a profile to Klaviyo's Back in Stock notification for a specific variant.
// The variant ID must be passed as the raw Shopify numeric ID; this function
// wraps it in the catalog-variant format Klaviyo expects for Shopify stores.
// Silently no-ops if email, variantId, or publicKey are missing.
async function klaviyoSubscribeBackInStock(email, variantId, publicKey) {
  if (!email || !variantId || !publicKey) return;
  try {
    const res = await fetch(
      `https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${encodeURIComponent(publicKey)}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', revision: '2024-10-15' },
        body: JSON.stringify({
          data: {
            type: 'back-in-stock-subscription',
            attributes: {
              channels: ['EMAIL'],
              profile: {
                data: {
                  type: 'profile',
                  attributes: { email },
                },
              },
            },
            relationships: {
              variant: {
                data: {
                  type: 'catalog-variant',
                  // Klaviyo's Shopify catalog variant ID format
                  id: `$shopify:::$default:::${variantId}`,
                },
              },
            },
          },
        }),
      }
    );
    if (!res.ok) console.warn('[Quiz] Klaviyo BIS subscribe HTTP', res.status, await res.text());
  } catch (err) {
    console.error('[Quiz] Klaviyo BIS subscribe error:', err);
  }
}

// ─── Analytics helpers ────────────────────────────────────────────────────────

/**
 * Push a GA4 custom event to the GTM dataLayer.
 * All quiz events share a `quiz_name` dimension so GA4 Funnel Explorations
 * and custom reports can filter to this quiz specifically.
 * Safe no-op when GTM / gtag.js is not present on the page.
 *
 * @param {string} eventName - GA4 event name (snake_case)
 * @param {Object} [params]  - Additional event parameters
 */
function _ga4(eventName, params) {
  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: eventName, ...(params || {}) });
  } catch (_) { /* non-blocking - never interrupt the quiz */ }
}

/**
 * Fire a Klaviyo track event WITHOUT requiring an email address.
 * Relies on the on-page _learnq snippet + Klaviyo's cookie-based identity.
 * Known visitors (anyone with a Klaviyo __kla_id cookie) will have the
 * event attached to their profile even if they skipped the capture step.
 * Silently skips if the Klaviyo snippet is not loaded.
 *
 * @param {string} eventName - Klaviyo metric name
 * @param {Object} [props]   - Event properties
 */
function _klaviyoTrackAnon(eventName, props) {
  try {
    if (typeof window._learnq !== 'undefined') {
      window._learnq.push(['track', eventName, props || {}]);
    }
  } catch (_) { /* non-blocking */ }
}

// ─── MAIN QUIZ COMPONENT ──────────────────────────────────────────────────────
function hairQuiz() {
  return {
    screen: 'intro',   // intro | question | capture | results
    currentStep: 0,
    answers: {},
    email: '',
    phone: '',
    emailConsent: false,
    smsConsent: false,
    productsLoading: false,
    productsError: false,
    emailError: '',
    submitting: false,
    results: null,

    get questions()       { return QUIZ_QUESTIONS; },
    get totalSteps()      { return QUIZ_QUESTIONS.length; },
    get currentQuestion() { return QUIZ_QUESTIONS[this.currentStep] || QUIZ_QUESTIONS[0]; },
    get currentAnswer()   { return this.answers[this.currentQuestion?.id]; },
    get canAdvance() {
      const a = this.currentAnswer;
      if (this.currentQuestion?.type === 'multi') return Array.isArray(a) && a.length > 0;
      return !!a;
    },

    init() {
      // Mark section as Alpine-ready - hides the static fallback and shows the real intro
      this.$el.classList.add('quiz-section--ready');

      // Apply sms default checked setting from customizer
      const cfg = window.QUIZ_CONFIG || {};
      this.smsConsent = cfg.smsDefaultChecked !== false; // defaults true

      // In the Shopify theme customizer, show the interactive intro so merchants
      // can preview the quiz. Skip sessionStorage restore in this context.
      if (window.Shopify?.designMode) {
        this.screen = 'intro';
        return;
      }

      // Skip intro screen if configured (e.g. LP pages where hero already primes the user)
      if (cfg.skipIntro) {
        this.screen = 'question';
        return;
      }

      // Restore in-progress session (e.g. accidental browser back)
      try {
        const saved = sessionStorage.getItem('pl_quiz_progress');
        if (saved) {
          const s = JSON.parse(saved);
          if (s.screen === 'question') {
            this.currentStep = s.currentStep || 0;
            this.answers     = s.answers || {};
            this.screen      = 'question';
          }
        }
      } catch (_) { /* ignore */ }
    },

    _saveProgress() {
      try {
        sessionStorage.setItem('pl_quiz_progress', JSON.stringify({
          screen: this.screen,
          currentStep: this.currentStep,
          answers: this.answers,
        }));
      } catch (_) { /* ignore */ }
    },

    startQuiz() {
      this.screen = 'question';
      _ga4('quiz_started', { quiz_name: 'hair_match_quiz' });
      this.$nextTick(() => this._focusRef('questionHeading'));
    },

    advance() {
      if (!this.canAdvance) return;
      this._saveProgress();

      // Track the step that was just answered before moving forward
      const q      = this.currentQuestion;
      const answer = this.currentAnswer;
      _ga4('quiz_step_completed', {
        quiz_name:   'hair_match_quiz',
        step_index:  this.currentStep,
        step_id:     q.id,
        step_answer: Array.isArray(answer) ? answer.join(',') : (answer || ''),
      });

      if (this.currentStep < QUIZ_QUESTIONS.length - 1) {
        this.currentStep++;
        this.$nextTick(() => this._focusRef('questionHeading'));
      } else {
        this.screen = 'capture';
        _ga4('quiz_capture_shown', { quiz_name: 'hair_match_quiz' });
        this.$nextTick(() => this._focusRef('captureHeading'));
      }
    },

    goBack() {
      if (this.currentStep > 0) {
        this.currentStep--;
        this.$nextTick(() => this._focusRef('questionHeading'));
      } else {
        this.screen = 'intro';
      }
    },

    selectOption(value) {
      const q = this.currentQuestion;
      if (q.type === 'multi') {
        const prev = Array.isArray(this.answers[q.id]) ? this.answers[q.id] : [];
        this.answers = {
          ...this.answers,
          [q.id]: prev.includes(value)
            ? prev.filter(v => v !== value)
            : [...prev, value],
        };
      } else {
        this.answers = { ...this.answers, [q.id]: value };
      }
    },

    isSelected(value) {
      const a = this.currentAnswer;
      if (this.currentQuestion?.type === 'multi') return Array.isArray(a) && a.includes(value);
      return a === value;
    },

    async submitCapture() {
      this.emailError = '';
      const cfg          = window.QUIZ_CONFIG || {};
      const emailRequired = cfg.emailRequired !== false; // default true

      if (emailRequired && (!this.email || !this.email.includes('@') || !this.email.includes('.'))) {
        this.emailError = 'Please enter a valid email address.';
        return;
      }

      // If email provided but malformed, still warn even when not required
      if (this.email && (!this.email.includes('@') || !this.email.includes('.'))) {
        this.emailError = 'Please enter a valid email address.';
        return;
      }

      this.submitting = true;

      const r = computeResults(this.answers);

      // Build base event props from quiz answers
      const baseEventProps = {
        ...r.klaviyoProfile,
        email_consent: this.emailConsent,
        sms_consent:   this.smsConsent,
      };

      // Start the product fetch but DO NOT block the results screen on it.
      // products.json is a multi-megabyte response on large collections; awaiting
      // it here used to leave the shopper on a spinner for several seconds.
      const productsPromise = fetchProductsForMethod(r.method, this.answers);

      // ── Render results immediately ──────────────────────────────────────
      this._storeLocal(r);

      window.PL_QUIZ_EMAIL      = this.email || '';
      window.PL_QUIZ_PUBLIC_KEY = cfg.klaviyoPublicKey || '';

      try { sessionStorage.removeItem('pl_quiz_progress'); } catch (_) {}

      _ga4('quiz_email_submitted', {
        quiz_name:          'hair_match_quiz',
        recommended_method: r.method,
        email_consent:      String(this.emailConsent),
        sms_consent:        String(this.smsConsent),
      });
      _ga4('quiz_completed', {
        quiz_name:          'hair_match_quiz',
        recommended_method: r.method,
        budget_range:       this.answers.budget    || '',
        hair_type:          this.answers.hair_type || '',
        hair_goal:          Array.isArray(this.answers.goal)
                              ? this.answers.goal.join(',')
                              : (this.answers.goal || ''),
        email_captured:     'true',   // string: GA4 text dimensions drop booleans
      });

      this.results         = r;
      this.submitting      = false;
      this.productsLoading = true;
      this.productsError   = false;
      this.screen          = 'results';

      this.$nextTick(() => this._focusRef('resultsHeading'));

      // ── Products + Klaviyo resolve in the background ─────────────────────
      let fetchedProducts = [];
      try {
        fetchedProducts = await productsPromise;
      } catch (err) {
        console.error('[Quiz] product fetch failed:', err);
      }

      this.productsLoading = false;
      this.productsError   = fetchedProducts.length === 0;

      if (fetchedProducts.length) {
        this.$nextTick(() => this._renderProducts(fetchedProducts, r));
      }

      const colorHint  = this.answers.hair_color || '';
      const eventProps = {
        ...baseEventProps,
        ...buildProductEventProps(fetchedProducts, colorHint, r.collectionUrl),
      };

      // Not awaited - the shopper is already on the results screen.
      // These helpers swallow their own errors; the catch is belt-and-braces.
      Promise.all([
        (async () => {
          await klaviyoIdentify(this.email, eventProps, cfg.klaviyoPublicKey);
          await klaviyoTrackEvent(
            this.email,
            cfg.klaviyoEventName || 'Quiz Completed',
            eventProps,
            cfg.klaviyoPublicKey
          );
        })(),
        this.emailConsent && cfg.klaviyoEmailListId
          ? klaviyoSubscribeEmail(this.email, cfg.klaviyoEmailListId, cfg.klaviyoPublicKey)
          : Promise.resolve(),
        this.smsConsent && this.phone && cfg.smsListId
          ? klaviyoSubscribeSMS(this.email, this.phone, cfg.smsListId, cfg.klaviyoPublicKey)
          : Promise.resolve(),
      ]).catch(err => console.error('[Quiz] Klaviyo dispatch failed:', err));
    },

    // Injects dynamically fetched product cards into the results grid.
    // Builds card HTML in JS, sets innerHTML, then calls Alpine.initTree()
    // so each card's x-data="quizProductCard(...)" is properly initialised.
    _renderProducts(products, r) {
      const grid = this.$refs.productGrid;
      if (!grid) return;

      if (!products.length) {
        grid.innerHTML = `
          <p class="quiz-products-empty">
            Browse the full
            <a href="${r.collectionUrl}" class="quiz-link">${r.methodLabel} collection</a>
            to find your match.
          </p>`;
        grid.classList.remove('quiz-product-grid--single');
        return;
      }

      // Toggle single-card layout class so CSS can center + constrain width
      grid.classList.toggle('quiz-product-grid--single', products.length === 1);

      const colorHint = this.answers.hair_color || '';
      grid.innerHTML  = products.map((p, i) => buildCardHTML(p, i === 0, colorHint)).join('');

      if (window.Alpine) {
        Alpine.initTree(grid);
      }
    },

    _storeLocal(r) {
      try {
        const entry = {
          ts: Date.now(),
          email: this.email,
          method: r.method,
          profile: r.klaviyoProfile,
        };
        const list = JSON.parse(localStorage.getItem('pl_quiz_subs') || '[]');
        list.unshift(entry);
        localStorage.setItem('pl_quiz_subs', JSON.stringify(list.slice(0, 50)));
      } catch (_) { /* ignore */ }
    },

    // Skip the email capture entirely - go straight to results without Klaviyo tracking
    async skipCapture() {
      if (this.submitting) return;
      this.submitting = true;
      try {
        const r = computeResults(this.answers);
        const cfg = window.QUIZ_CONFIG || {};

        // Non-blocking, same as submitCapture()
        const productsPromise = fetchProductsForMethod(r.method, this.answers);

        _ga4('quiz_capture_skipped', {
          quiz_name:          'hair_match_quiz',
          recommended_method: r.method,
        });
        _ga4('quiz_completed', {
          quiz_name:          'hair_match_quiz',
          recommended_method: r.method,
          budget_range:       this.answers.budget    || '',
          hair_type:          this.answers.hair_type || '',
          hair_goal:          Array.isArray(this.answers.goal)
                                ? this.answers.goal.join(',')
                                : (this.answers.goal || ''),
          email_captured:     'false',
        });

        // Klaviyo: fire against cookie-based identity for known visitors.
        // recommended_collection_url is included so flow SMS links resolve.
        _klaviyoTrackAnon(cfg.klaviyoEventName || 'Quiz Completed', {
          ...r.klaviyoProfile,
          recommended_collection_url: r.collectionUrl || '',
          email_captured: false,
        });

        this.results         = r;
        this.productsLoading = true;
        this.productsError   = false;
        this.screen          = 'results';
        this.$nextTick(() => this._focusRef('resultsHeading'));

        let fetchedProducts = [];
        try { fetchedProducts = await productsPromise; }
        catch (e) { console.error('[Quiz] product fetch failed:', e); }

        this.productsLoading = false;
        this.productsError   = fetchedProducts.length === 0;
        if (fetchedProducts.length) {
          this.$nextTick(() => this._renderProducts(fetchedProducts, r));
        }
      } catch (err) {
        console.error('[Quiz] skipCapture error:', err);
      } finally {
        this.submitting = false;
      }
    },

    resetQuiz() {
      this.screen       = 'intro';
      this.currentStep  = 0;
      this.answers      = {};
      this.email        = '';
      this.phone        = '';
      this.emailConsent = false;
      this.smsConsent   = (window.QUIZ_CONFIG?.smsDefaultChecked !== false);
      this.productsLoading = false;
      this.productsError   = false;
      this.emailError   = '';
      this.results      = null;
      sessionStorage.removeItem('pl_quiz_progress');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },

    _focusRef(ref) {
      const el = this.$refs[ref];
      if (el) el.focus();
    },
  };
}

// ─── PRODUCT CARD COMPONENT ───────────────────────────────────────────────────
/**
 * Two calling conventions are supported:
 *
 * NEW  (dynamic JS-rendered cards):
 *   quizProductCard(cardId, colorHint)
 *   cardId:    key into window.__quizCardData, set by buildCardHTML()
 *   colorHint: quiz color answer string used for COLOR_MAP matching
 *
 * LEGACY (Liquid-rendered quiz-product-card.liquid snippet):
 *   quizProductCard(variantMap, swatches, lengths, swatchImages)
 *   Kept for backwards compatibility if the snippet is still in use.
 */
function quizProductCard(cardIdOrVariantMap, colorHintOrSwatches, lengths, swatchImages) {
  let variantMap, swatches, resolvedLengths, resolvedSwatchImages, colorHint;

  if (typeof cardIdOrVariantMap === 'string') {
    // New dynamic path
    const data = (window.__quizCardData || {})[cardIdOrVariantMap] || {};
    variantMap           = data.variantMap   || {};
    swatches             = data.swatches     || [];
    resolvedLengths      = data.lengths      || ['__default__'];
    resolvedSwatchImages = data.swatchImages || {};
    colorHint            = colorHintOrSwatches || '';
  } else {
    // Legacy Liquid path
    variantMap           = cardIdOrVariantMap  || {};
    swatches             = colorHintOrSwatches || [];
    resolvedLengths      = lengths             || ['__default__'];
    resolvedSwatchImages = swatchImages        || {};
    colorHint            = '';
  }

  return {
    variantMap:   variantMap,
    swatches:     swatches,
    lengths:      resolvedLengths,
    swatchImages: resolvedSwatchImages,
    colorHint:    colorHint,

    selectedColor:    '',
    selectedLength:   '',
    addedToCart:      false,
    addingToCart:     false,
    bisSubscribed:    false,   // true once BIS subscription has fired for this variant
    bisVariantId:     null,    // tracks which variant we already subscribed BIS for

    get variantKey()  { return `${this.selectedColor}___${this.selectedLength}`; },
    get currentVariant()          { return this.variantMap[this.variantKey] || null; },
    get currentVariantId()        { return this.currentVariant?.id || null; },
    get currentVariantAvailable() { return this.currentVariant?.available ?? false; },
    get currentPrice()            { return this.currentVariant?.price || 0; },
    get currentImage()            { return this.swatchImages?.[this.selectedColor] || null; },
    get selectedSwatchName() {
      return this.swatches.find(s => s.value === this.selectedColor)?.name || '';
    },

    initCard() {
      if (this.swatches.length) this.selectedColor  = this.swatches[0].value;
      if (this.lengths.length)  this.selectedLength = this.lengths[0];

      // Enhanced color matching: use COLOR_MAP patterns against "Number + Name" variant names
      const hint = this.colorHint || window.PL_QUIZ_COLOR_HINT;
      if (hint) {
        const match = findBestColorSwatch(this.swatches, hint);
        if (match) this.selectedColor = match.value;
      }

      // After color matching, auto-subscribe BIS if the resolved variant is OOS
      this.$nextTick(() => this._autoSubscribeBIS());
    },

    // Called whenever the selected variant changes. Silently subscribes the
    // quiz email to Klaviyo's Back in Stock for this variant if it is OOS.
    // De-duped by bisVariantId so we never double-subscribe the same variant.
    _autoSubscribeBIS() {
      const variantId = this.currentVariantId;
      if (!variantId || this.currentVariantAvailable) return;
      if (this.bisVariantId === variantId) return; // already subscribed for this one

      const email     = window.PL_QUIZ_EMAIL      || '';
      const publicKey = window.PL_QUIZ_PUBLIC_KEY || '';
      if (!email || !publicKey) return;

      this.bisVariantId  = variantId;
      this.bisSubscribed = true;
      klaviyoSubscribeBackInStock(email, variantId, publicKey);

      _ga4('quiz_bis_subscribed', {
        quiz_name:  'hair_match_quiz',
        variant_id: variantId,
      });
    },

    selectColor(value) {
      this.selectedColor = value;
      if (!this.isLengthAvailable(this.selectedLength)) {
        const first = this.lengths.find(l => this.variantMap[`${value}___${l}`]?.available);
        if (first) this.selectedLength = first;
      }
      this.$nextTick(() => this._autoSubscribeBIS());
    },

    selectLength(len) {
      if (this.isLengthAvailable(len)) this.selectedLength = len;
      this.$nextTick(() => this._autoSubscribeBIS());
    },

    isLengthAvailable(len) {
      return this.variantMap[`${this.selectedColor}___${len}`]?.available ?? false;
    },

    formatPrice(dollars) {
      if (!dollars && dollars !== 0) return '';
      // Read the store's money format from the theme's body data attribute.
      // The theme writes: <body data-currency-format="{{ shop.money_format }}">
      // Shopify format tokens: {{amount}}, {{amount_no_decimals}},
      //   {{amount_with_comma_separator}}, {{amount_no_decimals_with_comma_separator}}
      const fmt = document.body?.dataset?.currencyFormat || '${{amount}}';
      const amount = typeof dollars === 'string' ? parseFloat(dollars) : dollars;
      if (isNaN(amount)) return '';

      const withDecimals    = amount.toFixed(2);
      const noDecimals      = Math.round(amount).toString();
      const commaSep        = amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      const commaNoDecimals = Math.round(amount).toLocaleString('en-US');

      return fmt
        .replace('{{amount_no_decimals_with_comma_separator}}', commaNoDecimals)
        .replace('{{amount_with_comma_separator}}',             commaSep)
        .replace('{{amount_no_decimals}}',                      noDecimals)
        .replace('{{amount}}',                                  withDecimals);
    },

    /* Fires when the shopper leaves the results screen for the PDP via the
       "View" link. Previously untracked, which made quiz_add_to_cart look like
       the only conversion path off the results card - it is not. Most quiz
       users appear to take this route and then convert on the product page,
       where the add-to-cart is attributed to the native add_to_cart with no
       quiz context. This closes that gap.
       Non-blocking: the anchor navigates normally regardless. */
    viewProduct() {
      _ga4('quiz_view_product', {
        quiz_name:  'hair_match_quiz',
        variant_id: this.currentVariantId,
        price:      this.currentPrice,
        color:      this.selectedSwatchName,
        length:     this.selectedLength !== '__default__' ? this.selectedLength : '',
      });
    },

    async addToCart() {
      if (!this.currentVariantId || !this.currentVariantAvailable || this.addingToCart) return;
      this.addingToCart = true;

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: [{
              id: this.currentVariantId,
              quantity: 1,
              properties: { _source: 'hair-match-quiz' },
            }],
          }),
        });

        if (!res.ok) throw new Error('Add to cart failed');

        // Theme's CartDrawer listens on document.documentElement, not document
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
          bubbles: true,
          detail: { openMiniCart: true },
        }));

        this.addedToCart = true;
        setTimeout(() => { this.addedToCart = false; }, 2800);

        // GA4: add-to-cart directly from quiz results
        _ga4('quiz_add_to_cart', {
          quiz_name:  'hair_match_quiz',
          variant_id: this.currentVariantId,
          price:      this.currentPrice,
          color:      this.selectedSwatchName,
          length:     this.selectedLength !== '__default__' ? this.selectedLength : '',
        });
      } catch (err) {
        console.error('[Quiz] Add to cart error:', err);
      } finally {
        this.addingToCart = false;
      }
    },
  };
}

// ─── Alpine Registration ───────────────────────────────────────────────────────
// Register hairQuiz via alpine:init so it's available BEFORE Alpine processes
// x-data attributes - fixes the defer timing race in Shopify (including customizer).
// quizProductCard is kept on window because it receives args via x-data="quizProductCard(...)".

window.quizProductCard = quizProductCard;

document.addEventListener('alpine:init', () => {
  if (window.Alpine) {
    Alpine.data('hairQuiz', hairQuiz);
  }
});

// Fallback: if Alpine already initialised before this script ran,
// manually init any quiz sections that weren't processed yet.
function _quizInitFallback() {
  if (!window.Alpine) return;
  document.querySelectorAll('.quiz-section').forEach(el => {
    if (el._x_dataStack) return;

    Alpine.data('hairQuiz', hairQuiz);

    // Alpine.initTree() skips any node that already carries _x_marker, and Alpine
    // stamps that marker even when the x-data expression failed to evaluate (i.e.
    // when it walked this section before hairQuiz was registered). Without tearing
    // the subtree down first, initTree() here is a silent no-op and the quiz never
    // boots. destroyTree() clears the markers and any half-bound directives.
    if (typeof Alpine.destroyTree === 'function') {
      Alpine.destroyTree(el);
    } else {
      delete el._x_marker;
      el.querySelectorAll('*').forEach(node => { delete node._x_marker; });
    }

    Alpine.initTree(el);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', _quizInitFallback);
} else {
  _quizInitFallback();
}
/* ── External render API (used by the landing-page quiz) ──────────────────────
   Additive only. Exposes the existing card builder and an Alpine hydrate helper
   so other sections (e.g. the LP campaign quiz) can render the same product
   cards without duplicating the engine. Does NOT change the main quiz. */
window.PLQuizEngine = window.PLQuizEngine || {};
window.PLQuizEngine.buildCard = buildCardHTML;
window.PLQuizEngine.hydrate = function (el) {
  if (el && window.Alpine && typeof window.Alpine.initTree === 'function') {
    window.Alpine.initTree(el);
  }
};